import dayjs from 'dayjs';
import { useState, useCallback, useMemo } from 'react';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { SEARCH_PARAM_KEY_MAP } from 'business/shift-report/report/hooks/constants';
import { buildBooleanFilter } from 'business/shift-report/report/services/build-boolean-filter';
import Routes from 'config/routes';
import {
  GetReportsQuery,
  Module_Enum,
  useGetReportsLazyQuery,
  useGetReportsBeingEditedLazyQuery,
  useGetManagerReportHistoryLazyQuery,
  useGetReportsToReviewLazyQuery,
  ShiftReport_Bool_Exp,
} from 'generated/graphql';
import { SIMPLE_DATE_FORMAT } from 'technical/string/formatter';

enum ReportListRoutes {
  All = Routes.ReportList,
  InProgress = Routes.ReportListInProgress,
  ToValidate = Routes.ReportListToValidate,
  Validated = Routes.ReportListValidated,
}

const parseFilterString = (filterString: string | null) => {
  if (!filterString) {
    return {};
  }

  const splittedFilters = filterString.split('&');

  const filterRecords = splittedFilters.map((filter) => {
    const keyValueSplit = filter.split('=');
    return {
      field: keyValueSplit[0],
      value: keyValueSplit?.[1],
    };
  });

  return Object.values(SEARCH_PARAM_KEY_MAP).reduce<
    Record<string, string | undefined>
  >((acc, field) => {
    let value = filterRecords.find((record) => record.field === field)?.value;
    if (
      field === SEARCH_PARAM_KEY_MAP.startDate ||
      field === SEARCH_PARAM_KEY_MAP.endDate
    ) {
      value = value ? dayjs(value).format(SIMPLE_DATE_FORMAT) : undefined;
    }

    return {
      ...acc,
      [field]: value,
    };
  }, {});
};

const useAllReportListQueries = (
  currentConstructionSiteId: string,
  fillReportsData: (data: GetReportsQuery['shiftReport']) => void,
  filterString: string | null,
) => {
  const filters = parseFilterString(filterString);
  const gqlHeaders = useGraphqlApiHeaders(Module_Enum.ShiftReport);

  const queryFilters: ShiftReport_Bool_Exp = buildBooleanFilter([
    ['operatorId', filters.operator],
    ['shiftLeaderId', filters.shiftLeader],
    ['shiftEngineerId', filters.shiftEngineer],
    ['shiftManagerId', filters.shiftManager],
    ['shiftId', filters.shift],
    ['teamId', filters.team],
  ]);

  if (filters.startDate && filters.endDate) {
    queryFilters.date = {
      _gte: filters.startDate,
      _lte: filters.endDate,
    };
  }

  const [getReportsQuery, { called: getReportsCalled }] =
    useGetReportsLazyQuery({
      variables: {
        constructionSiteId: currentConstructionSiteId,
        date: filters?.startDate,
      },
      fetchPolicy: 'network-only',
      onCompleted: (result) => fillReportsData(result.shiftReport),
      context: {
        headers: gqlHeaders,
      },
    });
  const [getReportsBeingEdited, { called: getReportsBeingEditedCalled }] =
    useGetReportsBeingEditedLazyQuery({
      variables: {
        constructionSiteId: currentConstructionSiteId,
      },
      fetchPolicy: 'network-only',
      onCompleted: (result) => fillReportsData(result.shiftReports),
      context: {
        headers: gqlHeaders,
      },
    });
  const [getManagerReportHistory, { called: getManagerReportHistoryCalled }] =
    useGetManagerReportHistoryLazyQuery({
      variables: {
        constructionSiteId: currentConstructionSiteId,
        filter: queryFilters,
      },
      fetchPolicy: 'network-only',
      onCompleted: (result) => fillReportsData(result.shiftReports),
      context: {
        headers: gqlHeaders,
      },
    });
  const [getReportsToReview, { called: getReportsToReviewCalled }] =
    useGetReportsToReviewLazyQuery({
      variables: {
        constructionSiteId: currentConstructionSiteId,
      },
      fetchPolicy: 'network-only',
      onCompleted: (result) => fillReportsData(result.shiftReports),
      context: {
        headers: gqlHeaders,
      },
    });

  return {
    reports: {
      query: getReportsQuery,
      called: getReportsCalled,
    },
    reportsBeingEdited: {
      query: getReportsBeingEdited,
      called: getReportsBeingEditedCalled,
    },
    managerReportsHistory: {
      query: getManagerReportHistory,
      called: getManagerReportHistoryCalled,
    },
    reportsToReview: {
      query: getReportsToReview,
      called: getReportsToReviewCalled,
    },
  };
};

const useReportIds = (
  reportsData: GetReportsQuery['shiftReport'] | undefined,
  currentReportId: string,
) => {
  const currentReportIndex = useMemo(() => {
    if (!reportsData) {
      return -1;
    }
    return reportsData.findIndex((report) => {
      return report.id === currentReportId;
    });
  }, [currentReportId, reportsData]);

  const previousReportId = useMemo(() => {
    if (!reportsData || currentReportIndex === -1) {
      return undefined;
    }

    return reportsData?.[currentReportIndex + 1]?.id;
  }, [currentReportIndex, reportsData]);

  const nextReportId = useMemo(() => {
    if (!reportsData || currentReportIndex === -1) {
      return undefined;
    }

    return reportsData?.[currentReportIndex - 1]?.id;
  }, [currentReportIndex, reportsData]);

  return { previousReportId, nextReportId };
};

export const usePreviousAndNextReportIds = (
  reportList: string,
  filters: string | null,
  currentReportId: string,
  currentConstructionSiteId: string,
) => {
  const [reportsData, setReportsData] = useState<
    GetReportsQuery['shiftReport'] | undefined
  >(undefined);

  const fillReportsData = useCallback(
    (data: GetReportsQuery['shiftReport']) => {
      if (reportsData) {
        return;
      }

      setReportsData(data);
    },
    [reportsData],
  );

  const {
    reports,
    reportsBeingEdited,
    managerReportsHistory,
    reportsToReview,
  } = useAllReportListQueries(
    currentConstructionSiteId,
    fillReportsData,
    filters,
  );

  if (reportList === ReportListRoutes.All && !reports.called) {
    reports.query();
  }
  if (
    reportList === ReportListRoutes.InProgress &&
    !reportsBeingEdited.called
  ) {
    reportsBeingEdited.query();
  }
  if (
    reportList === ReportListRoutes.Validated &&
    !managerReportsHistory.called
  ) {
    managerReportsHistory.query();
  }
  if (reportList === ReportListRoutes.ToValidate && !reportsToReview.called) {
    reportsToReview.query();
  }

  return useReportIds(reportsData, currentReportId);
};
